import { PaginationQueryParams, ResponseSuccess } from '@forwardflow/types'
import { apiGet, apiPost } from '../Axios/configs'
import applyPaginationQueryParams from '@forwardflow/helpers/utils/applyPaginationQueryParams'

export interface ConversationItem {
   isIncoming: boolean
   itemType: string
   id: string
   meta: {
      appointment: {
         date: string
         status: string
         platform: string
      }
   }
   email: string
   phoneNo: string
   lastName: string
   firstName: string
   stageInfo: {
      id: string
      name: string
      stageGroup: {
         id: string
         name: string
         type: string
      }
   }
   lastConversation: {
      date: string
      type: string
      content: string
   }
   unreadMessages: number
}

export interface UserInfo {
   id: string
   avatar?: string
   name?: string
}

export interface MessageMeta {
   isLiked: boolean
   isDisLiked: boolean
   totalFeedbacks: number
}

export interface Objection {
   id: string
   content: string
   objectionType: string
   rates: string
}

export interface CallMeta {
   callStatus: string
}

export interface CallSummaryMeta {
   duration: number
}

export interface AppointmentMeta {
   id: string
   appointmentDate: string
   appointmentLocation: string
   appointmentType: string
   platform: string
}

export interface TimelineEvent {
   id: string
   content: string
   ts: string
}

export interface MessageData {
   id?: string
   sender?: string
   info: UserInfo
   content: string
   ts?: string
   meta?: MessageMeta
   objection?: Objection
}

export interface CallData {
   id: string
   type: string
   info: UserInfo
   content: string
   ts: string
   meta: CallMeta
}

export interface CallSummaryData {
   id: string
   attendees: UserInfo[]
   timeline: TimelineEvent[]
   meta: CallSummaryMeta
}

export interface AppointmentData {
   id: string
   content: string
   meta: AppointmentMeta
   ts: string
   attendees: UserInfo[]
}

export interface AppointmentMissedData {
   id: string
   content: string
   ts: string
}

export type Interaction =
   | Partial<MessageData>
   | CallData
   | CallSummaryData
   | AppointmentData
   | AppointmentMissedData

export class ConversationService {
   static async getRecentConversations(
      queryParams: PaginationQueryParams
   ): Promise<[]> {
      const response: { data: [] } = await apiGet({
         apiPath: `/recent-conversations?${applyPaginationQueryParams(
            queryParams
         )}`,
      })

      const contacts = response || []
      return contacts.data
   }

   // Request next conversion (Demo)

   static async requestNextConversation(leadId: number) {
      const response = await apiPost<ResponseSuccess<ConversationItem[]>>({
         apiPath: `/leads/${leadId}/forward-campaign-step`,
      })
      return response.data
   }

   // Request previous conversion (Demo)

   static async requestPreviousConversation(leadId: number) {
      const response = await apiPost<ResponseSuccess<ConversationItem[]>>({
         apiPath: `/leads/${leadId}/backward-campaign-step`,
      })
      return response.data
   }

   // Reset conversation (Demo)
   static async resetConversation(leadId: number) {
      const response = await apiPost<ResponseSuccess<ConversationItem[]>>({
         apiPath: `/leads/${leadId}/force-reset-demo-people`,
      })
      return response.data
   }

   // Reset all conversation (Demo)

   static async resetAllConversation() {
      const response = await apiPost<ResponseSuccess<ConversationItem[]>>({
         apiPath: `/active-lead-sources/force-reset-demo-source`,
      })
      return response.data
   }

   // send message (Demo)

   static async sendDemoMessage(
      leadId: number,
      message: string
   ): Promise<ConversationItem[]> {
      const response = await apiPost<ResponseSuccess<ConversationItem[]>>({
         apiPath: `/leads/${leadId}/send-demo-sms`,
         data: { message: message },
      })
      return response.data
   }

   static async getConversation(
      queryParams: PaginationQueryParams,
      leadId: number
   ) {
      const response = await apiGet<ResponseSuccess<ConversationItem[]>>({
         apiPath: `/conversations-timeline/${leadId}?${applyPaginationQueryParams(
            queryParams
         )}&hideExternalContent=true`,
      })
      return response.data.reverse()
   }

   static async sendMessage(
      leadId: number,
      message: string
   ): Promise<ConversationItem[]> {
      const response = await apiPost<ResponseSuccess<ConversationItem[]>>({
         apiPath: `/leads/${leadId}/send-sms`,
         data: { message: message },
      })
      return response.data
   }

   static async getCallTranscript(id: number) {
      if (!id) return
      const response = await apiGet<ResponseSuccess<any>>({
         apiPath: `/call-transcript/${id}`,
      })
      return response.data
   }

   static async getRecordingPresignedUrl(objKey: string) {
      const response = await apiGet<ResponseSuccess<string>>({
         apiPath: `/storage/presigned-url?objKey=${objKey}`,
      })
      return response.data
   }

   static async setDemoRealtorPhone(leadId: number, phone: string) {
      const response = await apiPost<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/set-demo-realtor-phone`,
         data: { phone: phone },
      })
      return response.data
   }

   static async clearDemoRealtorPhone(leadId: number) {
      const response = await apiPost<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/clear-demo-realtor-phone`,
      })
      return response.data
   }

   static async resetRealtorTimeout(leadId: number) {
      const response = await apiPost<ResponseSuccess<any>>({
         apiPath: `/realtor-workflow-manager/reset-appointment-timeout`,
         data: { personId: leadId },
      })
      return response.data
   }

   static async getDemoIncomingCallStatus(leadId: number) {
      const response = await apiGet<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/demo-incoming-call-status`,
      })
      return response.data
   }

   static async setDemoIncomingCallStatus(leadId: number, status: boolean) {
      const response = await apiPost<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/set-demo-incoming-call?isDemo=${status}`,
      })
      return response.data
   }

   static async getSetAppointmentStatus(leadId: number) {
      const response = await apiGet<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/demo-lead-set-appointment-status`,
      })
      return response.data
   }

   static async updateSetAppointmentStatus(leadId: number, status: boolean) {
      const response = await apiPost<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/set-appointment-flag-for-demo-lead?isEnabled=${status}`,
      })
      return response.data
   }
   // Set demo inbox app lead
   static async setDemoInboxAppLead(leadId: number, inboxAppLeadId: number) {
      const response = await apiPost<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/set-demo-inbox-app-lead`,
         data: { inboxAppLeadId },
      })
      return response.data
   }

   // Set demo inbox app enabled
   static async setDemoInboxAppEnabled(leadId: number, isEnabled: boolean) {
      const response = await apiPost<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/set-demo-inbox-app-enabled`,
         data: { isEnabled },
      })
      return response.data
   }

   // Get demo inbox app lead
   static async getDemoInboxAppLead(leadId: number) {
      const response = await apiGet<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/get-demo-inbox-app-lead`,
      })
      return response.data
   }

   // Get demo inbox app enabled
   static async getDemoInboxAppEnabled(leadId: number) {
      const response = await apiGet<ResponseSuccess<any>>({
         apiPath: `/leads/${leadId}/get-demo-inbox-app-enabled`,
      })
      return response.data
   }
}
